import { useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { supabase, createSessionBoundSupabaseClient } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import {
  CHECKLIST_VALUES_V2_FEATURE,
  getSyncClientBuild,
  subscribeSyncCanaryFeatureDecisions,
  syncCanaryFeatureDecisionStore,
  SyncCanaryFeatureService,
} from '@/services/sync/SyncCanaryFeatureService';

type Decision = { scope: string; enabled: boolean | null };
let storage: MMKV | null = null;
const getStorage = () => storage ??= new MMKV({ id: 'checklist-observation-capability-v1' });
const cacheKey = (companyId: string, userId: string, build: number) => JSON.stringify([companyId, userId, build]);
const readDecision = (key: string): boolean | null => {
  try {
    const value = getStorage().getString(key);
    return value === 'on' ? true : value === 'off' ? false : null;
  } catch { return null; }
};

/** Uma autorização offline pertence à empresa, identidade e build que a consultaram. */
export const useChecklistObservationCapability = (input: {
  companyId?: string | null;
  userId?: string | null;
  profileCompanyId?: string | null;
  checklistId?: string | null;
  isOnline: boolean;
}): boolean | null => {
  const companyId = input.companyId?.trim() || '';
  const userId = input.userId?.trim() || '';
  const profileCompanyId = input.profileCompanyId?.trim() || '';
  const build = getSyncClientBuild();
  const profileUserId = globalState.user.profile.get()?.id || '';
  const sessionUserId = globalState.user.session.get()?.user?.id || '';
  const authenticated = globalState.user.isAuthenticated.get() === true;
  const valid = Boolean(authenticated && companyId && userId && userId === profileUserId && userId === sessionUserId && companyId === profileCompanyId && build >= 128);
  const scope = JSON.stringify([companyId, userId, profileCompanyId, input.checklistId, build]);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const [decision, setDecision] = useState<Decision>({ scope: '', enabled: null });

  useEffect(() => {
    if (!valid) return;
    let active = true;
    let request = 0;
    const key = cacheKey(companyId, userId, build);
    const identityMatches = () => active && scopeRef.current === scope &&
      globalState.user.isAuthenticated.get() === true &&
      globalState.user.user.get()?.id === userId &&
      globalState.user.profile.get()?.empresa_id === companyId &&
      globalState.user.profile.get()?.id === userId &&
      globalState.user.session.get()?.user?.id === userId;
    const cachedDecision = () => {
      try {
        if (syncCanaryFeatureDecisionStore.get({ companyId, featureKey: CHECKLIST_VALUES_V2_FEATURE, clientBuild: build }) === false) return false;
      } catch { /* Somente o cache da identidade pode liberar edição offline. */ }
      return readDecision(key);
    };
    setDecision({ scope, enabled: identityMatches() ? cachedDecision() : null });
    const unsubscribeDecisions = subscribeSyncCanaryFeatureDecisions(observed => {
      if (observed.enabled !== false || observed.companyId !== companyId ||
        observed.featureKey !== CHECKLIST_VALUES_V2_FEATURE || observed.clientBuild !== build || !identityMatches()) return;
      request += 1;
      try { getStorage().set(key, 'off'); } catch { /* A revogação online não depende do cache. */ }
      setDecision({ scope, enabled: false });
    });
    const feature = new SyncCanaryFeatureService({
      async isEnabled(args) {
        if (!identityMatches()) throw new Error('A identidade do checklist mudou.');
        const { data: auth, error: authError } = await supabase.auth.getSession();
        if (authError || !auth.session?.access_token || auth.session.user.id !== userId || !identityMatches()) {
          throw new Error('A sessão do checklist mudou.');
        }
        const client = createSessionBoundSupabaseClient(auth.session.access_token);
        const { data, error } = await client.rpc('app_guincho_feature_enabled_v1', {
          p_empresa_id: args.companyId,
          p_feature_key: args.featureKey,
          p_client_build: args.clientBuild,
        });
        if (error) throw error;
        if (!identityMatches()) throw new Error('A identidade do checklist mudou.');
        return data === true;
      },
    });
    const refresh = () => {
      if (!input.isOnline || !identityMatches()) return;
      const currentRequest = ++request;
      void feature.isEnabled(companyId, CHECKLIST_VALUES_V2_FEATURE, build).then(enabled => {
        if (!identityMatches() || currentRequest !== request) return;
        try { getStorage().set(key, enabled ? 'on' : 'off'); } catch { /* A decisão online permanece válida sem cache. */ }
        setDecision({ scope, enabled });
        try { syncCanaryFeatureDecisionStore.set({ companyId, featureKey: CHECKLIST_VALUES_V2_FEATURE, clientBuild: build, enabled }); }
        catch { /* A decisão desta identidade permanece válida sem persistência compartilhada. */ }
      }).catch(() => {
        if (identityMatches() && currentRequest === request) setDecision({ scope, enabled: cachedDecision() });
      });
    };
    refresh();
    const subscription = AppState.addEventListener('change', state => { if (state === 'active') refresh(); });
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.addEventListener('focus', refresh);
    return () => {
      active = false;
      unsubscribeDecisions();
      subscription.remove();
      if (Platform.OS === 'web' && typeof window !== 'undefined') window.removeEventListener('focus', refresh);
    };
  }, [scope, valid, companyId, userId, build, input.isOnline]);

  if (!valid || globalState.user.isAuthenticated.get() !== true ||
    globalState.user.user.get()?.id !== userId || globalState.user.profile.get()?.empresa_id !== companyId ||
    globalState.user.profile.get()?.id !== userId || globalState.user.session.get()?.user?.id !== userId) return false;
  return decision.scope === scope ? decision.enabled : null;
};
